import React from 'react';
import { safeJsonLd } from '@c/StructuredData';
import Link from 'next/link';
import Head from 'next/head';
import Image from 'next/image';
import SEO from '@c/SEO';
import FAQSection from '@c/landing/FAQSection';
import LandingPageFooter from '@c/landing/LandingPageFooter';
import LandingPageHeader from '@c/landing/LandingPageHeader';
import HeroBanner from '@c/landing/HeroBanner';
import Carousel from '@ui/Carousel';
import SafeImage from '@ui/SafeImage';
import { getPopularActiveProductsOver400 } from '@util/firestore/products';
import { ProductDocument } from '@util/types/firestore/products';
import { GetServerSideProps } from 'next';
import { formatCurrency } from '@util/index';
import { getProductUrl } from '@util/urlHelpers';
import { getConditionShortLabel } from '@util/conditions/conditionShortLabel';

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

interface Influencer {
  name: string;
  role: string;
  quote: string;
  imageSrc: string;
  imageAlt: string;
  hasVideo: boolean;
  videoUrl?: string;
}

interface Category {
  name: string;
  imageSrc: string;
  imageAlt: string;
  url: string;
}

interface CreatorStore {
  name: string;
  tagline: string;
  imageSrc: string;
  imageAlt: string;
  linkHref: string;
}

interface ComparisonRow {
  feature: string;
  gearFocus: string;
  ebay: string;
  facebook: string;
}

// ============================================================================
// MOCK DATA
// ============================================================================

const influencers: Influencer[] = [
  {
    name: 'Gerald Undone',
    role: 'YouTube Gear Reviewer',
    quote: 'GearFocus helps me move gear quickly when my studio gets overloaded.',
    imageSrc: '/landings/creators/gerald-undone.jpg',
    imageAlt: 'Gerald Undone headshot',
    hasVideo: true,
    videoUrl: 'https://youtu.be/y588GAQ7x5E?si=Kw28bqAyb9RzzykZ',
  },
  {
    name: 'Jeven Dovey',
    role: 'Travel & Indie Filmmaker',
    quote: 'I rotate my filmmaking kit a lot. GearFocus makes it easy and safe.',
    imageSrc: '/landings/creators/jeven-dovey.jpg',
    imageAlt: 'Jeven Dovey headshot',
    hasVideo: true,
    videoUrl: 'https://youtu.be/XbRjT36ra0k?si=N078YTbaL_A-hBny',
  },
  {
    name: 'Vanessa Joy',
    role: 'Wedding and Portrait Photography',
    quote: 'Selling my extra gear has never been easier. Creator-friendly and low fees — finally.',
    imageSrc: '/landings/creators/vanessa-joy.jpg',
    imageAlt: 'Vanessa Joy headshot',
    hasVideo: true,
    videoUrl: 'https://youtu.be/ROj4QVhvBcU?si=VB3hQLaHMUC2dY_F',
  },
  {
    name: 'Kevin Kolbe',
    role: 'Content Creator & Filmmaker',
    quote: 'GearFocus is where I offload my old gear and find deals on upgrades.',
    imageSrc: '/landings/creators/kevin-kolbe.jpg',
    imageAlt: 'Kevin Kolbe headshot',
    hasVideo: true,
    videoUrl: 'https://youtu.be/yuBSKXKEgv8?si=pu1VpI--d7GpGjIN',
  },
];

const categories: Category[] = [
  {
    name: 'Cameras',
    imageSrc: '/categories/cameras.webp',
    imageAlt: 'Browse Cameras',
    url: '/c/digital-cameras',
  },
  {
    name: 'Lenses',
    imageSrc: '/categories/lenses.webp',
    imageAlt: 'Shop For Lenses',
    url: '/c/photo-video-lenses',
  },
  {
    name: 'Video',
    imageSrc: '/categories/video.webp',
    imageAlt: 'Shop Video Equipment',
    url: '/c/video-camcorders',
  },
  {
    name: 'Lighting',
    imageSrc: '/categories/lighting.webp',
    imageAlt: 'Browse Lighting Equipment',
    url: '/c/lighting-studio',
  },
  {
    name: 'Drones',
    imageSrc: '/categories/drones.webp',
    imageAlt: 'Shop For Drones',
    url: '/c/drones-aerial-imaging',
  },
  {
    name: 'Audio',
    imageSrc: '/categories/audio.webp',
    imageAlt: 'Browse Audio Equipment',
    url: '/c/professional-audio',
  },
  {
    name: 'Vintage',
    imageSrc: '/categories/vintage.webp',
    imageAlt: 'Shop Vintage Equipment',
    url: '/c/vintage-camera-equipment',
  },
  {
    name: 'Computers',
    imageSrc: '/categories/computers.webp',
    imageAlt: 'Browse Computers',
    url: '/c/computers-electronics',
  },
  {
    name: 'Accessories',
    imageSrc: '/categories/accessories.webp',
    imageAlt: 'Shop Accessories',
    url: '/c/photo-video-accessories',
  },
];

const creatorStores: CreatorStore[] = [
  {
    name: 'Vanessa Joy',
    tagline: 'Wedding Photography Gear',
    imageSrc: '/landings/stores/vanessa-joy-store.jpg',
    imageAlt: 'Vanessa Joy store banner',
    linkHref: '/shop-seller/VanessaJoy',
  },
  {
    name: 'Jeven Dovey',
    tagline: 'Travel & indie filmmaking kits',
    imageSrc: '/landings/stores/jeven-dovey-store.jpg',
    imageAlt: 'Jeven Dovey store banner',
    linkHref: '/shop-seller/jevens-gear-shop-3059',
  },
  {
    name: 'Kevin Kolbe',
    tagline: 'Content creator essentials',
    imageSrc: '/landings/stores/kevin-kolbe-store.jpg',
    imageAlt: 'Kevin Kolbe store banner',
    linkHref: '/shop-seller/kevin-kolbes-gear-shop-5032',
  },
];

const comparisonRows: ComparisonRow[] = [
  {
    feature: 'Seller Fees',
    gearFocus: '8.5%',
    ebay: '~13–15%',
    facebook: '0% (but more hassle)',
  },
  {
    feature: 'Camera-Only Marketplace',
    gearFocus: 'Yes',
    ebay: 'No',
    facebook: 'No',
  },
  {
    feature: 'Buyer Protection',
    gearFocus: 'Yes',
    ebay: 'Limited',
    facebook: 'None',
  },
  {
    feature: 'Secure Payments',
    gearFocus: 'Yes',
    ebay: 'Yes',
    facebook: 'No',
  },
  {
    feature: 'Scam Risk',
    gearFocus: 'Low',
    ebay: 'Medium',
    facebook: 'High',
  },
  {
    feature: 'Creator Community',
    gearFocus: 'Yes',
    ebay: 'No',
    facebook: 'No',
  },
];

const faqItems = [
  {
    question: 'What happens if my item arrives damaged or not as described?',
    answer:
      'Every eligible order is covered by GearFocus Buyer Protection. If something arrives damaged or misrepresented, we facilitate return and refund.',
  },
  {
    question: 'Do you ship internationally?',
    answer: 'GearFocus currently supports US & Canada. International expansion is coming soon.',
  },
  {
    question: 'How do I know the seller is legit?',
    answer:
      'Every seller account is verified and monitored. We flag suspicious listings and intervene on scams.',
  },
  {
    question: 'How fast do I get paid?',
    answer:
      'Funds are released shortly after delivery confirmation or the end of the protection window.',
  },
  {
    question: 'What are your fees?',
    answer: 'A simple 8.5% total fee. No listing fees. No hidden charges.',
  },
  {
    question: 'Do I need a business license or LLC?',
    answer: 'No. Individuals are welcome to sell casually or regularly.',
  },
  {
    question: 'What gear can I list?',
    answer:
      'Cameras, lenses, audio gear, lighting, drones, tripods, gimbals, monitors, and filmmaking accessories.',
  },
];

// ============================================================================
// SECTION COMPONENTS
// ============================================================================

const WhatIsGearFocusSection: React.FC = () => {
  const features = [
    {
      title: 'Only Camera & Video Gear',
      body: 'No random junk. Just photography, video, lighting, audio, and accessories.',
      icon: (
        <svg className="w-10 h-10 md:w-16 md:h-16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
      ),
    },
    {
      title: 'Lower Seller Fees',
      body: 'Keep more of what you earn with our simple 8.5% total fee structure.',
      icon: (
        <svg className="w-10 h-10 md:w-16 md:h-16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
    },
    {
      title: 'Buyer Protection',
      body: "Orders are covered if they arrive damaged, misrepresented, or don't arrive at all.",
      icon: (
        <svg className="w-10 h-10 md:w-16 md:h-16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
        </svg>
      ),
    },
  ];

  return (
    <section className="bg-gray-200 py-20 md:py-24">
      <div className="max-w-7xl md:max-w-default mx-auto px-4 md:px-[4rem] xl:px-default-padding-x">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h2 className="text-[28px] md:text-[3.2rem] font-semibold leading-tight mb-6">
            <span className="md:hidden">A Marketplace Built Just for Creators</span>
            <span className="hidden md:inline">A Marketplace Built<br />Just for Creators</span>
          </h2>
          <p className="text-2xl md:text-[1.5rem] text-gray-700 leading-normal md:leading-relaxed">
            Most marketplaces are cluttered, confusing, or full of scams. GearFocus is built
            exclusively for photographers, filmmakers, and content creators — giving you a safer,
            simpler way to buy and sell the gear you actually use.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {features.map((feature, idx) => (
            <div
              key={idx}
              className="bg-white rounded-xl p-8 shadow-md hover:shadow-lg transition"
            >
              <div className="text-brand-primary mb-4">{feature.icon}</div>
              <h3 className="text-2xl md:text-2xl font-semibold md:font-bold text-brand-secondary mb-3">{feature.title}</h3>
              <p className="text-2xl md:text-[1.5rem] text-gray-600">{feature.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const BuyerSection: React.FC<{ trendingProducts: ProductDocument[] }> = ({ trendingProducts = [] }) => {
  // Create carousel items for mobile view matching homepage card format
  const carouselItems = trendingProducts.map((product, idx) => {
    const productUrl = getProductUrl(product.slug);

    return (
      <div key={product.sku || idx} className="flex flex-col items-center">
        <Link
          className="rounded-[10px] flex justify-center items-center overflow-clip h-[14.4rem] w-[14.4rem] sm:h-[21.2rem] sm:w-[21.2rem]"
          href={productUrl}
        >
          <SafeImage
            title={`View ${product.title}`}
            alt={product.title}
            src={product.thumbnail}
            height={288}
            width={288}
            className="hover:scale-105"
            loading="lazy"
          />
        </Link>
        <div className="mt-[1.6rem] flex h-full w-full flex-col justify-start gap-[0.2rem]">
          {product.bumps > 0 && (
            <p className="text-[1.3rem] font-semibold text-brand-primary">GearFocus Bump</p>
          )}
          <Link
            href={productUrl}
            title={`View ${product.title} on GearFocus`}
            className="line-clamp-2 min-h-[3rem] text-[1.8rem] font-normal leading-[2.4rem]"
          >
            <span>{product.title}</span>
          </Link>
          <div className="self-baseline">
            {product.condition && (
              <p className="text-[1.1rem] leading-[2.4rem]">{getConditionShortLabel(product.condition)}</p>
            )}
            {product.on_sale && !product.out_of_stock ? (
              <div className="gap-[0.8rem] sm:flex-row">
                <p className="text-[1.4rem] font-normal leading-[2.4rem] text-gray-600">
                  <span className="line-through">{formatCurrency(product.og_price)}</span>
                  <span className="block md:inline text-brand-red text-[1.2rem] font-medium md:pl-[0.5rem]">
                    ${Math.round(Number(product.og_price - product.price))} price drop
                  </span>
                </p>
                <p className="text-[1.8rem] font-bold leading-[2.4rem] text-brand-secondary">
                  {formatCurrency(product.price)}
                </p>
              </div>
            ) : (
              <p className="text-[1.8rem] font-bold leading-[2.4rem] text-brand-secondary">
                {formatCurrency(product.price)}
              </p>
            )}
          </div>
        </div>
      </div>
    );
  });

  return (
    <section className="bg-white py-20 md:py-24">
      <div className="max-w-7xl md:max-w-default mx-auto px-4 md:px-[4rem] xl:px-default-padding-x">
        {/* Trending Products */}
        <div id="popular-right-now" className="mb-12">
          <h3 className="text-[28px] md:text-4xl font-semibold leading-tight text-center mb-4 md:mb-8">Popular Right Now</h3>

          {/* Mobile: Swipeable Carousel */}
          <div className="md:hidden">
            <Carousel items={carouselItems} />
          </div>

          {/* Desktop: Grid using same card format as carousel */}
          <div className="hidden md:grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6">
            {trendingProducts.map((product, idx) => {
              const productUrl = getProductUrl(product.slug);

              return (
                <div key={product.sku || idx} className="flex flex-col items-center">
                  <Link
                    className="rounded-[10px] flex justify-center items-center overflow-clip h-[14.4rem] w-[14.4rem] sm:h-[21.2rem] sm:w-[21.2rem]"
                    href={productUrl}
                  >
                    <SafeImage
                      title={`View ${product.title}`}
                      alt={product.title}
                      src={product.thumbnail}
                      height={288}
                      width={288}
                      className="hover:scale-105"
                      loading="lazy"
                    />
                  </Link>
                  <div className="mt-[1.6rem] flex h-full w-full flex-col justify-start gap-[0.2rem]">
                    {product.bumps > 0 && (
                      <p className="text-[1.3rem] font-semibold text-brand-primary">GearFocus Bump</p>
                    )}
                    <Link
                      href={productUrl}
                      title={`View ${product.title} on GearFocus`}
                      className="line-clamp-2 min-h-[3rem] text-[1.8rem] font-normal leading-[2.4rem]"
                    >
                      <span>{product.title}</span>
                    </Link>
                    <div className="self-baseline">
                      {product.condition && (
                        <p className="text-[1.1rem] leading-[2.4rem]">{getConditionShortLabel(product.condition)}</p>
                      )}
                      {product.on_sale && !product.out_of_stock ? (
                        <div className="gap-[0.8rem] sm:flex-row">
                          <p className="text-[1.4rem] font-normal leading-[2.4rem] text-gray-600">
                            <span className="line-through">{formatCurrency(product.og_price)}</span>
                            <span className="block md:inline text-brand-red text-[1.2rem] font-medium md:pl-[0.5rem]">
                              ${Math.round(Number(product.og_price - product.price))} price drop
                            </span>
                          </p>
                          <p className="text-[1.8rem] font-bold leading-[2.4rem] text-brand-secondary">
                            {formatCurrency(product.price)}
                          </p>
                        </div>
                      ) : (
                        <p className="text-[1.8rem] font-bold leading-[2.4rem] text-brand-secondary">
                          {formatCurrency(product.price)}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Trust Badges */}
        <div className="flex flex-wrap justify-center gap-4 mb-10">
          <span className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-brand-lighter-orange border border-brand-primary text-base md:text-xl text-brand-secondary">
            <svg className="w-5 h-5 md:w-7 md:h-7 text-brand-primary" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            Covered by Buyer Protection
          </span>
          <span className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-brand-lighter-orange border border-brand-primary text-base md:text-xl text-brand-secondary">
            <svg className="w-5 h-5 md:w-7 md:h-7 text-brand-primary" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            Shop from real creators
          </span>
          <span className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-brand-lighter-orange border border-brand-primary text-base md:text-xl text-brand-secondary">
            <svg className="w-5 h-5 md:w-7 md:h-7 text-brand-primary" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            Curated camera-only marketplace
          </span>
        </div>

        {/* CTA */}
        <div className="text-center mb-24">
          <Link href="/products">
            <button className="bg-[#ED7523] hover:opacity-90 text-white cursor-pointer py-3 px-6 rounded-[10px] font-medium text-[16px]">
              Shop All Deals
            </button>
          </Link>
        </div>

        {/* Category Tiles */}
        <div>
          <h3 className="text-[28px] md:text-4xl font-semibold leading-tight text-center mb-12">Browse by Category</h3>
          {/* Mobile: 6 categories in 2x3 grid (Cameras, Lenses, Video, Audio, Lighting, All Products) */}
          <div className="md:hidden grid grid-cols-3 gap-4 max-w-[600px] mx-auto">
            {categories
              .filter((cat) => ['Cameras', 'Lenses', 'Video', 'Audio', 'Lighting'].includes(cat.name))
              .map((cat, idx) => (
                <Link
                  key={idx}
                  href={cat.url}
                  className="relative h-[16rem] overflow-hidden rounded-[1rem] border border-black hover:bg-gradient-orange hover:text-brand-white bg-white"
                >
                  <span className="absolute top-8 w-full text-center text-xl md:text-lg font-semibold">
                    {cat.name}
                  </span>
                  <div className="absolute bottom-0 w-full flex justify-center">
                    <div className="relative w-[120px] h-[96px]">
                      <Image
                        src={cat.imageSrc}
                        alt={cat.imageAlt}
                        fill
                        className="object-contain object-bottom"
                        priority
                      />
                    </div>
                  </div>
                </Link>
              ))}
            {/* All Products link using Accessories image */}
            <Link
              href="/products"
              className="relative h-[16rem] overflow-hidden rounded-[1rem] border border-black hover:bg-gradient-orange hover:text-brand-white bg-white"
            >
              <span className="absolute top-8 w-full text-center text-xl md:text-lg font-semibold">
                All Products
              </span>
              <div className="absolute bottom-0 w-full flex justify-center">
                <div className="relative w-[120px] h-[96px]">
                  <Image
                    src="/categories/accessories.webp"
                    alt="Browse All Products"
                    fill
                    className="object-contain object-bottom"
                    priority
                  />
                </div>
              </div>
            </Link>
          </div>
          {/* Desktop: All 9 categories */}
          <div className="hidden md:flex flex-wrap justify-center gap-[1.6rem]">
            {categories.map((cat, idx) => (
              <Link
                key={idx}
                href={cat.url}
                className="relative min-h-[20rem] min-w-[20rem] overflow-hidden rounded-[1rem] border border-black hover:bg-gradient-orange hover:text-brand-white bg-white"
              >
                <span className="absolute top-8 w-full px-[2rem] text-center font-semibold">
                  {cat.name}
                </span>
                <div className="absolute bottom-0 w-full flex justify-center">
                  <div className="relative w-[200px] h-[160px]">
                    <Image
                      src={cat.imageSrc}
                      alt={cat.imageAlt}
                      fill
                      className="object-contain"
                      priority
                    />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

const SellerSection: React.FC = () => {
  const steps = [
    {
      title: 'List Your Gear',
      body: "Take a few photos, add a description, and set your price. We'll help with pricing suggestions if needed.",
      number: '1',
    },
    {
      title: 'Ship With Confidence',
      body: 'Use recommended shipping options with built-in tracking so everyone stays protected.',
      number: '2',
    },
    {
      title: 'Get Paid Fast',
      body: 'Once the buyer confirms delivery or the protection window passes, funds are released straight to your bank.',
      number: '3',
    },
  ];

  return (
    <section id="sell-today" className="bg-gray-200 py-20 md:py-24">
      <div className="max-w-7xl md:max-w-default mx-auto px-4 md:px-[4rem] xl:px-default-padding-x">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h2 className="text-[28px] md:text-[3.2rem] font-semibold leading-tight mb-6">
            <span className="md:hidden">Turn Your Camera Gear Into Cash</span>
            <span className="hidden md:inline">Turn Your Camera Gear<br />Into Cash</span>
          </h2>
          <p className="text-2xl md:text-[1.5rem] text-gray-700 leading-normal md:leading-relaxed">
            List your gear in minutes, tap into a creator-focused audience, and get paid securely.
          </p>
        </div>

        {/* 3-Step Process */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
          {steps.map((step, idx) => (
            <div key={idx} className="relative">
              <div className="bg-white rounded-xl p-8 shadow-md h-full">
                <div className="w-16 h-16 rounded-full bg-brand-primary text-white font-bold text-3xl flex items-center justify-center mb-6">
                  {step.number}
                </div>
                <h3 className="text-2xl md:text-2xl font-semibold md:font-bold text-brand-secondary mb-4">{step.title}</h3>
                <p className="text-2xl md:text-[1.5rem] text-gray-600">{step.body}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Benefit Line */}
        <div className="text-center mb-10">
          <div className="flex flex-wrap justify-center gap-x-8 gap-y-2 text-2xl md:text-[1.5rem] text-gray-700">
            <span className="flex items-center gap-2">
              <svg className="w-5 h-5 text-brand-primary" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              Only 8.5% fees
            </span>
            <span className="flex items-center gap-2">
              <svg className="w-5 h-5 text-brand-primary" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              Creator community
            </span>
            <span className="flex items-center gap-2">
              <svg className="w-5 h-5 text-brand-primary" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              Real human support
            </span>
          </div>
        </div>

        {/* Seller Testimonial */}
        <div className="max-w-2xl mx-auto mb-10">
          <div className="bg-white rounded-xl p-8 shadow-md border-l-4 border-brand-primary">
            <p className="text-2xl md:text-[1.5rem] text-gray-700 italic mb-4 leading-normal md:leading-relaxed">
              &quot;I sold my Canon R5 kit in under two days. GearFocus fees were way lower than eBay, and
              the whole process felt creator-friendly.&quot;
            </p>
            <p className="text-2xl md:text-base font-semibold text-brand-secondary">— Caleb, Filmmaker</p>
          </div>
        </div>

        {/* CTA */}
        <div className="text-center">
          <Link href="/sell">
            <button className="bg-[#ED7523] hover:opacity-90 text-white cursor-pointer py-3 px-6 rounded-[10px] font-medium text-[16px]">
              Start Selling
            </button>
          </Link>
        </div>
      </div>
    </section>
  );
};

const InfluencerTrustSection: React.FC = () => {
  return (
    <section className="bg-white py-20 md:py-24">
      <div className="max-w-7xl md:max-w-default mx-auto px-4 md:px-[4rem] xl:px-default-padding-x">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h2 className="text-[28px] md:text-[3.2rem] font-semibold leading-tight mb-6">
            <span className="md:hidden">Trusted by Creators You Already Follow</span>
            <span className="hidden md:inline">Trusted by Creators<br />You Already Follow</span>
          </h2>
          <p className="text-2xl md:text-[1.5rem] text-gray-700 leading-normal md:leading-relaxed">
            Top photographers and filmmakers use GearFocus to buy and sell their gear.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {influencers.map((influencer, idx) => (
            <div
              key={idx}
              className="bg-white rounded-xl border-2 border-gray-200 p-8 shadow-md hover:shadow-lg transition"
            >
              <div className="flex items-start gap-4 mb-6">
                <Image
                  src={influencer.imageSrc}
                  alt={influencer.imageAlt}
                  width={80}
                  height={80}
                  className="rounded-full object-cover border-2 border-brand-primary"
                />
                <div className="flex-1">
                  <h3 className="text-2xl md:text-2xl font-bold text-brand-secondary">{influencer.name}</h3>
                  <p className="text-2xl md:text-[1.5rem] text-gray-600">{influencer.role}</p>
                </div>
              </div>
              <p className="text-2xl md:text-[1.5rem] text-gray-700 italic mb-6 leading-normal md:leading-relaxed">&ldquo;{influencer.quote}&rdquo;</p>
              {influencer.hasVideo && influencer.videoUrl && (
                <a
                  href={influencer.videoUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-lg md:text-base font-semibold text-brand-primary hover:text-brand-orange transition"
                >
                  <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" />
                  </svg>
                  Watch Video
                </a>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const ComparisonSection: React.FC = () => {
  return (
    <section className="bg-gray-200 py-20 md:py-24">
      <div className="max-w-5xl md:max-w-default mx-auto px-4 md:px-[4rem] xl:px-default-padding-x">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h2 className="text-[28px] md:text-[3.2rem] font-semibold leading-tight mb-6">
            <span className="md:hidden">Why Not Just Use eBay or Facebook?</span>
            <span className="hidden md:inline">Why Not Just Use<br />eBay or Facebook?</span>
          </h2>
          <p className="text-2xl md:text-[1.5rem] text-gray-700 leading-normal md:leading-relaxed">
            We built GearFocus because traditional marketplaces weren&apos;t made for creators.
          </p>
        </div>

        {/* Mobile: Comparison Cards */}
        <div className="md:hidden space-y-6 mb-10">
          {comparisonRows.map((row, idx) => (
            <div key={idx} className="bg-white rounded-xl shadow-lg p-6">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">{row.feature}</h3>
              <div className="space-y-3">
                <div className="flex justify-between items-center p-4 bg-brand-lighter-orange rounded-lg">
                  <span className="text-2xl font-semibold text-gray-700">GearFocus</span>
                  <span className="text-2xl font-bold text-brand-primary">{row.gearFocus}</span>
                </div>
                <div className="flex justify-between items-center p-4 bg-gray-50 rounded-lg">
                  <span className="text-2xl font-semibold text-gray-700">eBay</span>
                  <span className="text-2xl text-gray-600">{row.ebay}</span>
                </div>
                <div className="flex justify-between items-center p-4 bg-gray-50 rounded-lg">
                  <span className="text-2xl font-semibold text-gray-700">Facebook</span>
                  <span className="text-2xl text-gray-600">{row.facebook}</span>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Desktop: Comparison Table */}
        <div className="hidden md:block bg-white rounded-xl shadow-lg overflow-hidden mb-10">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50 border-b-2 border-gray-200">
                  <th className="text-left py-5 px-6 text-xl font-bold text-gray-700">Feature</th>
                  <th className="py-5 px-6 text-xl font-bold bg-brand-primary text-white">GearFocus</th>
                  <th className="py-5 px-6 text-xl font-bold text-gray-700">eBay</th>
                  <th className="py-5 px-6 text-xl font-bold text-gray-700">Facebook</th>
                </tr>
              </thead>
              <tbody>
                {comparisonRows.map((row, idx) => (
                  <tr key={idx} className="border-b border-gray-200">
                    <td className="py-5 px-6 text-xl font-semibold text-gray-700">{row.feature}</td>
                    <td className="py-5 px-6 text-xl text-center font-bold text-brand-primary bg-brand-lighter-orange">
                      {row.gearFocus}
                    </td>
                    <td className="py-5 px-6 text-xl text-center text-gray-600">{row.ebay}</td>
                    <td className="py-5 px-6 text-xl text-center text-gray-600">{row.facebook}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* CTA */}
        <div className="text-center">
          <Link href="/sell">
            <button className="bg-[#ED7523] hover:opacity-90 text-white cursor-pointer py-3 px-6 rounded-[10px] font-medium text-[16px]">
              Start Selling On GearFocus
            </button>
          </Link>
        </div>
      </div>
    </section>
  );
};

const CreatorStoresSection: React.FC = () => {
  return (
    <section className="bg-white py-20 md:py-24">
      <div className="max-w-7xl md:max-w-default mx-auto px-4 md:px-[4rem] xl:px-default-padding-x">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h2 className="text-[28px] md:text-[3.2rem] font-semibold leading-tight mb-6">
            <span className="md:hidden">Shop Gear From Real Creator Stores</span>
            <span className="hidden md:inline">Shop Gear<br />From Real Creator Stores</span>
          </h2>
          <p className="text-2xl md:text-[1.5rem] text-gray-700 leading-normal md:leading-relaxed">
            Browse curated gear drops directly from your favorite creators.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {creatorStores.map((store, idx) => (
            <Link
              key={idx}
              href={store.linkHref}
              className="group bg-white rounded-xl overflow-hidden border-2 border-gray-200 hover:border-brand-primary transition"
            >
              <div className="aspect-video relative bg-gray-100">
                <Image
                  src={store.imageSrc}
                  alt={store.imageAlt}
                  fill
                  className="object-cover group-hover:scale-105 transition"
                />
              </div>
              <div className="p-6">
                <h3 className="text-2xl md:text-xl font-bold text-brand-secondary mb-2">{store.name}</h3>
                <p className="text-2xl md:text-[1.5rem] text-gray-600 mb-4">{store.tagline}</p>
                <span className="text-base font-semibold text-brand-primary group-hover:underline">
                  View Store →
                </span>
              </div>
            </Link>
          ))}

          {/* Explore All Card */}
          <Link
            href="/products"
            className="group bg-linear-to-br from-brand-lighter-orange to-white rounded-xl overflow-hidden border-2 border-brand-primary hover:shadow-lg transition flex items-center justify-center p-8"
          >
            <div className="text-center">
              <div className="text-6xl mb-4">✨</div>
              <h3 className="text-2xl md:text-xl font-bold text-brand-secondary mb-2">Explore All Creator Stores</h3>
              <span className="text-lg md:text-base font-semibold text-brand-primary group-hover:underline">
                Browse All →
              </span>
            </div>
          </Link>
        </div>
      </div>
    </section>
  );
};

const FinalCTASection: React.FC = () => {
  return (
    <section className="bg-linear-to-br from-gray-200 via-orange-100 to-gray-200 py-20 md:py-28">
      <div className="max-w-4xl md:max-w-default mx-auto px-4 md:px-[4rem] xl:px-default-padding-x text-center">
        <h2 className="text-[28px] md:text-[3.2rem] font-semibold leading-tight mb-8">
          <span className="md:hidden">Ready to Join the Creator Marketplace?</span>
          <span className="hidden md:inline">Ready to Join<br />the Creator Marketplace?</span>
        </h2>
        <p className="text-2xl md:text-[1.5rem] text-gray-700 mb-12 leading-normal md:leading-relaxed">
          Whether you&apos;re upgrading your kit or clearing space, GearFocus makes buying and
          selling safe and simple.
        </p>

        <div className="flex flex-col sm:flex-row items-center justify-center gap-6 mb-10">
          <Link href="/sell">
            <button className="bg-[#ED7523] hover:opacity-90 text-white cursor-pointer py-3 px-6 rounded-[10px] font-medium text-[16px]">
              Start Selling
            </button>
          </Link>
          <Link href="/products">
            <button className="bg-[#ED7523] hover:opacity-90 text-white cursor-pointer py-3 px-6 rounded-[10px] font-medium text-[16px]">
              Shop Used Gear
            </button>
          </Link>
        </div>

        <p className="text-base text-gray-600">
          Have questions? Contact{' '}
          <a
            href="mailto:support@gearfocus.com"
            className="text-brand-primary hover:underline font-semibold"
          >
            support@gearfocus.com
          </a>
        </p>
      </div>
    </section>
  );
};

// ============================================================================
// MAIN PAGE COMPONENT
// ============================================================================

interface CreatorMarketplacePageProps {
  trendingProducts: ProductDocument[];
}

const CreatorMarketplacePage = ({ trendingProducts = [] }: CreatorMarketplacePageProps) => {
  return (
    <>
      <LandingPageHeader />
      <SEO
        title="Creator Marketplace - Buy & Sell Camera Gear | GearFocus"
        description="A creator-built marketplace for photographers and filmmakers. Lower fees, safer transactions, and gear from real creators you trust. 8.5% total seller fees, 100% Buyer Protection."
        ogImage="/landings/creator-marketplace/creator-hero-banner.png"
        keywords={[
          'creator marketplace',
          'buy camera gear',
          'sell camera gear',
          'photography equipment',
          'filmmaking gear',
          'used camera marketplace',
          'creator community',
          'safe gear marketplace',
          'verified sellers',
          'buyer protection'
        ]}
      />

      <Head>
        {/* WebPage Schema */}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: safeJsonLd({
              "@context": "https://schema.org",
              "@type": "WebPage",
              "name": "Creator Marketplace - Buy & Sell Camera Gear",
              "description": "A creator-built marketplace for photographers and filmmakers. Lower fees, safer transactions, and gear from real creators you trust.",
              "url": "https://gearfocus.com/lp/creator-marketplace",
              "specialty": "Camera and Filmmaking Equipment",
              "about": {
                "@type": "Thing",
                "name": "Camera and Video Equipment Marketplace"
              }
            })
          }}
        />

        {/* BreadcrumbList Schema */}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: safeJsonLd({
              "@context": "https://schema.org",
              "@type": "BreadcrumbList",
              "itemListElement": [
                {
                  "@type": "ListItem",
                  "position": 1,
                  "name": "Home",
                  "item": "https://gearfocus.com"
                },
                {
                  "@type": "ListItem",
                  "position": 2,
                  "name": "Landing Pages",
                  "item": "https://gearfocus.com/lp"
                },
                {
                  "@type": "ListItem",
                  "position": 3,
                  "name": "Creator Marketplace",
                  "item": "https://gearfocus.com/lp/creator-marketplace"
                }
              ]
            })
          }}
        />

        {/* OfferCatalog Schema - Enhanced with full product details */}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: safeJsonLd({
              "@context": "https://schema.org",
              "@type": "OfferCatalog",
              "name": "Creator Marketplace Trending Products",
              "description": "Popular camera and video equipment from verified creators",
              "itemListElement": trendingProducts.slice(0, 12).map((product, idx) => ({
                "@type": "Offer",
                "position": idx + 1,
                "itemOffered": {
                  "@type": "Product",
                  "name": product.title,
                  "image": product.thumbnail,
                  "sku": product.sku,
                  "brand": product.brand ? {
                    "@type": "Brand",
                    "name": product.brand
                  } : undefined,
                  "condition": product.condition ? `https://schema.org/${product.condition.includes('New') ? 'NewCondition' : 'UsedCondition'}` : "https://schema.org/UsedCondition",
                  "offers": {
                    "@type": "Offer",
                    "price": product.price,
                    "priceCurrency": "USD",
                    "availability": product.out_of_stock ? "https://schema.org/OutOfStock" : "https://schema.org/InStock",
                    "itemCondition": product.condition ? `https://schema.org/${product.condition.includes('New') ? 'NewCondition' : 'UsedCondition'}` : "https://schema.org/UsedCondition",
                    "seller": {
                      "@type": "Organization",
                      "name": "GearFocus Verified Seller"
                    }
                  }
                }
              }))
            })
          }}
        />

        {/* Organization Schema - GearFocus Brand Entity */}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: safeJsonLd({
              "@context": "https://schema.org",
              "@type": "Organization",
              "name": "GearFocus",
              "url": "https://gearfocus.com",
              "logo": "https://gearfocus.com/logo.svg",
              "description": "The trusted marketplace for buying and selling used camera and video equipment. Built by creators, for creators.",
              "sameAs": [
                "https://www.facebook.com/gearfocus",
                "https://www.instagram.com/gearfocus",
                "https://www.youtube.com/@gearfocus",
                "https://twitter.com/gearfocus"
              ],
              "contactPoint": {
                "@type": "ContactPoint",
                "contactType": "Customer Service",
                "email": "support@gearfocus.com",
                "areaServed": ["US", "CA"]
              }
            })
          }}
        />

        {/* FAQPage Schema - AI SEO Optimization */}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: safeJsonLd({
              "@context": "https://schema.org",
              "@type": "FAQPage",
              "mainEntity": [
                {
                  "@type": "Question",
                  "name": "What happens if my item arrives damaged or not as described?",
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "Every eligible order is covered by GearFocus Buyer Protection. If something arrives damaged or misrepresented, we facilitate return and refund."
                  }
                },
                {
                  "@type": "Question",
                  "name": "Do you ship internationally?",
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "GearFocus currently supports US & Canada. International expansion is coming soon."
                  }
                },
                {
                  "@type": "Question",
                  "name": "How do I know the seller is legit?",
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "Every seller account is verified and monitored. We flag suspicious listings and intervene on scams."
                  }
                },
                {
                  "@type": "Question",
                  "name": "How fast do I get paid?",
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "Funds are released shortly after delivery confirmation or the end of the protection window."
                  }
                },
                {
                  "@type": "Question",
                  "name": "What are your fees?",
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "A simple 8.5% total fee. No listing fees. No hidden charges."
                  }
                },
                {
                  "@type": "Question",
                  "name": "Do I need a business license or LLC?",
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "No. Individuals are welcome to sell casually or regularly."
                  }
                },
                {
                  "@type": "Question",
                  "name": "What gear can I list?",
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "Cameras, lenses, audio gear, lighting, drones, tripods, gimbals, monitors, and filmmaking accessories."
                  }
                }
              ]
            })
          }}
        />

        {/* VideoObject Schema - Creator Testimonials */}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: safeJsonLd({
              "@context": "https://schema.org",
              "@type": "ItemList",
              "itemListElement": [
                {
                  "@type": "VideoObject",
                  "position": 1,
                  "name": "Gerald Undone reviews GearFocus",
                  "description": "YouTube gear reviewer Gerald Undone shares his experience selling camera equipment on GearFocus",
                  "thumbnailUrl": "https://gearfocus.com/landings/creators/gerald-undone.jpg",
                  "contentUrl": "https://youtu.be/y588GAQ7x5E?si=Kw28bqAyb9RzzykZ",
                  "uploadDate": "2024-01-01",
                  "duration": "PT5M"
                },
                {
                  "@type": "VideoObject",
                  "position": 2,
                  "name": "Jeven Dovey - Travel Filmmaker on GearFocus",
                  "description": "Travel and indie filmmaker Jeven Dovey explains how GearFocus helps him rotate his filmmaking kit",
                  "thumbnailUrl": "https://gearfocus.com/landings/creators/jeven-dovey.jpg",
                  "contentUrl": "https://youtu.be/XbRjT36ra0k?si=N078YTbaL_A-hBny",
                  "uploadDate": "2024-01-01",
                  "duration": "PT4M"
                },
                {
                  "@type": "VideoObject",
                  "position": 3,
                  "name": "Vanessa Joy - Wedding Photographer Review",
                  "description": "Wedding and portrait photographer Vanessa Joy discusses selling camera gear with low fees on GearFocus",
                  "thumbnailUrl": "https://gearfocus.com/landings/creators/vanessa-joy.jpg",
                  "contentUrl": "https://youtu.be/ROj4QVhvBcU?si=VB3hQLaHMUC2dY_F",
                  "uploadDate": "2024-01-01",
                  "duration": "PT3M"
                },
                {
                  "@type": "VideoObject",
                  "position": 4,
                  "name": "Kevin Kolbe - Content Creator on GearFocus",
                  "description": "Content creator and filmmaker Kevin Kolbe shares how he uses GearFocus to offload old gear and find deals on upgrades",
                  "thumbnailUrl": "https://gearfocus.com/landings/creators/kevin-kolbe.jpg",
                  "contentUrl": "https://youtu.be/yuBSKXKEgv8?si=pu1VpI--d7GpGjIN",
                  "uploadDate": "2024-01-01",
                  "duration": "PT4M"
                }
              ]
            })
          }}
        />

        {/* ItemList Schema - Category Navigation */}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: safeJsonLd({
              "@context": "https://schema.org",
              "@type": "ItemList",
              "name": "Camera Equipment Categories",
              "description": "Browse camera and video equipment by category",
              "itemListElement": [
                {
                  "@type": "ListItem",
                  "position": 1,
                  "name": "Cameras",
                  "url": "https://gearfocus.com/c/digital-cameras"
                },
                {
                  "@type": "ListItem",
                  "position": 2,
                  "name": "Lenses",
                  "url": "https://gearfocus.com/c/photo-video-lenses"
                },
                {
                  "@type": "ListItem",
                  "position": 3,
                  "name": "Video",
                  "url": "https://gearfocus.com/c/video-camcorders"
                },
                {
                  "@type": "ListItem",
                  "position": 4,
                  "name": "Lighting",
                  "url": "https://gearfocus.com/c/lighting-studio"
                },
                {
                  "@type": "ListItem",
                  "position": 5,
                  "name": "Drones",
                  "url": "https://gearfocus.com/c/drones-aerial-imaging"
                },
                {
                  "@type": "ListItem",
                  "position": 6,
                  "name": "Audio",
                  "url": "https://gearfocus.com/c/professional-audio"
                },
                {
                  "@type": "ListItem",
                  "position": 7,
                  "name": "Vintage",
                  "url": "https://gearfocus.com/c/vintage-camera-equipment"
                },
                {
                  "@type": "ListItem",
                  "position": 8,
                  "name": "Computers",
                  "url": "https://gearfocus.com/c/computers-electronics"
                },
                {
                  "@type": "ListItem",
                  "position": 9,
                  "name": "Accessories",
                  "url": "https://gearfocus.com/c/photo-video-accessories"
                }
              ]
            })
          }}
        />
      </Head>

      <main className="min-h-screen bg-white">
        <HeroBanner
          backgroundImageUrlWeb="/landings/creator-marketplace/creator-hero-banner.png"
          backgroundImageUrlMobile="/landings/creator-marketplace/creator-hero-banner-mobile.png"
          backgroundSizing="auto"
          title="Creators:"
          headline="Buy & Sell Camera Gear With Total Confidence"
          subheadline="The trusted marketplace built by creators, for creators — lower fees, safer transactions, and gear from people who get it"
          highlightName=""
          ctaPrimaryLink={{url: "/products", target: "_self"}}
          ctaPrimaryLabel="Shop Used Gear"
          ctaSecondaryLink={{url: "/sell", target: "_self"}}
          ctaSecondaryLabel="Sell Your Gear"
          ctaBubble=""
          ratingText="4.7 Excellent on"
          ratingImageUrl="https://gearfocus.b-cdn.net/landings/rating-star.svg"
          ratingImageAlt="4.7 out of 5 star rating"
          trustpilotLogoUrl="https://gearfocus.b-cdn.net/landings/trustpilot-logo.svg"
          trustpilotLogoAlt="Trustpilot logo"
          trustStatement="Join 10,000+ creators trusting us today!"
        />
        <WhatIsGearFocusSection />
        <BuyerSection trendingProducts={trendingProducts} />
        <SellerSection />
        <InfluencerTrustSection />
        <ComparisonSection />
        <CreatorStoresSection />
        <FAQSection faqs={faqItems} />
        <FinalCTASection />
      </main>

      <LandingPageFooter />
    </>
  );
};

CreatorMarketplacePage.getLayout = function getLayout(page: React.ReactElement) {
  return page;
};

export default CreatorMarketplacePage;

export const getServerSideProps: GetServerSideProps<CreatorMarketplacePageProps> = async (context) => {
  context.res.setHeader(
    'Cache-Control',
    'public, max-age=0, s-maxage=60, stale-while-revalidate=300'
  );

  try {
    // Fetch more products than needed to ensure diversity after filtering
    const { results: products } = await getPopularActiveProductsOver400(30);

    // First pass: limit to max 2 products per seller for diversity
    const sellerCounts = new Map<string, number>();
    const diverseProducts: ProductDocument[] = [];
    const remainingProducts: ProductDocument[] = [];

    products.forEach((product: ProductDocument) => {
      const sellerId = product.seller_id;
      const currentCount = sellerCounts.get(sellerId) || 0;

      if (currentCount < 2) {
        sellerCounts.set(sellerId, currentCount + 1);
        diverseProducts.push(product);
      } else {
        remainingProducts.push(product);
      }
    });

    // If we don't have 5 products yet, add from remaining products to reach 5
    let selectedProducts = diverseProducts.slice(0, 5);
    if (selectedProducts.length < 5 && remainingProducts.length > 0) {
      const needed = 5 - selectedProducts.length;
      selectedProducts = [...selectedProducts, ...remainingProducts.slice(0, needed)];
    }

    // Return fetched ProductDocument array (no transformation needed - ProductCard handles the full document)
    return {
      props: {
        trendingProducts: selectedProducts,
      },
    };
  } catch (error) {
    // If query fails, return empty array
    console.error('Error fetching popular products:', error);
    return {
      props: {
        trendingProducts: [],
      },
    };
  }
};
